/** Report calculations are pure and never change the ledger. All amounts are cents. */
export interface ReportAccount {
    id: string;
    code: string | null;
    name: string;
    type: string;
    subtype: string | null;
    system_key: string | null;
    currency: string;
    parentId?: string | null;
}
export interface ReportPosting {
    id: string;
    journalId: string;
    accountId: string;
    date: string;
    debit: number;
    credit: number;
    source: string;
    sourceId: string | null;
    reference: string | null;
    memo: string | null;
    lineMemo?: string | null;
    customerId: string | null;
    vendorId: string | null;
    classId: string | null;
}
export type CashCategory = 'operating' | 'investing' | 'financing' | 'unclassified';
export interface ReportSnapshot {
    dimensions?: ReportDimensions;
    organization: {
        id: string;
        name: string;
        currency: string;
    };
    generatedAt: string;
    accounts: ReportAccount[];
    postings: ReportPosting[];
    corrections: {
        id: string;
        action: string;
        targetId: string;
        date: string;
        reason: string;
        actor: string | null;
    }[];
    statements: {
        id: string;
        accountId: string;
        start: string | null;
        end: string | null;
        provider: string | null;
    }[];
}
export const REPORT_BASIS = 'Posted general ledger; operational invoice/payment mirrors are excluded. This is not an automatic cash/accrual conversion.';
export type ReportDimensions = {
    customers: Record<string, string>;
    vendors: Record<string, string>;
    classes: Record<string, string>;
};
export function validDate(value: string): boolean { const d = new Date(value + 'T00:00:00Z'); return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(+d) && d.toISOString().slice(0, 10) === value; }
export function priorYear(value:string):string {if(!validDate(value))return '';const date=new Date(value+'T12:00:00Z'),month=date.getUTCMonth();date.setUTCFullYear(date.getUTCFullYear()-1);if(date.getUTCMonth()!==month)date.setUTCDate(0);return date.toISOString().slice(0,10);}
export function periodRows(data: ReportSnapshot, start: string, end: string) { return data.postings.filter(p => p.date >= start && p.date <= end); }
export function normalAmount(a: ReportAccount, p: ReportPosting) { return ['income', 'liability', 'equity'].includes(a.type) ? p.credit - p.debit : p.debit - p.credit; }
export function comparative(data: ReportSnapshot, start: string, end: string, priorStart: string, priorEnd: string, balance = false) {
    return data.accounts.map(a => { const lines = data.postings.filter(p => p.accountId === a.id); const sum = (s: string, e: string) => lines.filter(p => p.date >= s && p.date <= e).reduce((n, p) => n + normalAmount(a, p), 0); const current = sum(balance ? '0001-01-01' : start, end), prior = sum(balance ? '0001-01-01' : priorStart, priorEnd); return { ...a, current, prior, change: current - prior, percent: prior > 0 ? (current - prior) / prior * 100 : null }; });
}
export function monthly(data: ReportSnapshot, start: string, end: string) {
    if (!validDate(start) || !validDate(end) || start > end || Number(end.slice(0, 4)) - Number(start.slice(0, 4)) > 20)
        throw new Error('Choose a valid monthly range of no more than 20 years.');
    const periods: string[] = [];
    let month = start.slice(0, 7);
    while (month <= end.slice(0, 7)) {
        periods.push(month);
        const [y, m] = month.split('-').map(Number);
        month = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
    }
    return { periods, rows: data.accounts.filter(a => ['income', 'expense'].includes(a.type)).map(a => ({ ...a, amounts: periods.map(m => data.postings.filter(p => p.accountId === a.id && p.date >= start && p.date <= end && p.date.startsWith(m)).reduce((n, p) => n + normalAmount(a, p), 0)) })) };
}
export function eligibleCashAccounts(accounts: ReportAccount[]) { return accounts.filter(a => a.type === 'asset' && !['accounts_receivable','inventory_asset'].includes(a.system_key??'') && (a.system_key==='bank_checking'||a.system_key==='undeposited_funds'||['checking','savings','bank','cash','cash_on_hand','petty_cash','undeposited_funds','merchant_clearing','clearing','payment_clearing'].includes(a.subtype??''))); }
export function defaultCashAccounts(accounts: ReportAccount[]) { return eligibleCashAccounts(accounts).filter(a=>a.system_key==='bank_checking'||['checking','savings','bank','cash','cash_on_hand','petty_cash'].includes(a.subtype??'')).map(a=>a.id); }
export function suggestedCashCategory(a: ReportAccount): CashCategory {
    if (a.system_key === 'opening_balance_equity' || a.subtype === 'opening_balance_equity')
        return 'unclassified';
    if (a.type === 'equity' || a.subtype === 'line_of_credit' || /loan|long_term_debt/.test(a.subtype ?? ''))
        return 'financing';
    if (/fixed_asset|equipment|property|investment/.test(a.subtype ?? ''))
        return 'investing';
    if (a.system_key?.startsWith('uncategorized') || /uncategorized/i.test(a.name) || a.subtype === 'merchant_clearing')
        return 'unclassified';
    if (['income', 'expense'].includes(a.type) || ['accounts_receivable', 'accounts_payable', 'credit_card', 'inventory', 'sales_tax_payable'].includes(a.subtype ?? ''))
        return 'operating';
    return 'unclassified';
}
export function cashFlow(data: ReportSnapshot, start: string, end: string, cashIds: string[], mapping: Record<string, CashCategory> = {}) {
    const cash = new Set(cashIds), accounts = new Map(data.accounts.map(a => [a.id, a]));
    const opening = data.postings.filter(p => cash.has(p.accountId) && p.date < start).reduce((n, p) => n + p.debit - p.credit, 0);
    const closing = data.postings.filter(p => cash.has(p.accountId) && p.date <= end).reduce((n, p) => n + p.debit - p.credit, 0);
    const journals = new Map<string, ReportPosting[]>();
    const originals = new Map(periodRows(data,start,end).filter(p=>p.source==='bank_import').map(p=>[p.journalId,p.date]));
    for (const p of periodRows(data, start, end)) {
        // Category reviews retain separate GL journals but belong to the original
        // cash movement for classification. Never count the cash receipt again.
        const key=p.source==='bank_review'&&p.sourceId&&originals.get(p.sourceId)===p.date?p.sourceId:p.journalId;
        const arr = journals.get(key) ?? [];
        arr.push(p);
        journals.set(key, arr);
    }
    const totals: Record<CashCategory, number> = { operating: 0, investing: 0, financing: 0, unclassified: 0 };
    const details: {
        journalId: string;
        date: string;
        reference: string;
        category: CashCategory;
        amount: number;
        note: string;
    }[] = [];
    for (const [journalId, lines] of journals) {
        const movement = lines.filter(p => cash.has(p.accountId)).reduce((n, p) => n + p.debit - p.credit, 0);
        if (!movement)
            continue;
        let offsets = lines.filter(p => !cash.has(p.accountId) && (p.debit || p.credit));
        if(lines.some(p=>p.source==='bank_review')){
            const net=new Map<string,number>();
            for(const p of offsets)net.set(p.accountId,(net.get(p.accountId)??0)+p.debit-p.credit);
            offsets=offsets.filter((p,i)=>net.get(p.accountId)!==0&&offsets.findIndex(x=>x.accountId===p.accountId)===i);
        }
        const balanced = lines.reduce((n, p) => n + p.debit - p.credit, 0) === 0;
        // Mixed noncash entries cannot safely be allocated by proportion: flag for review.
        const categories = new Set(offsets.map(p => mapping[p.accountId] ?? suggestedCashCategory(accounts.get(p.accountId)!)));
        const category: CashCategory = balanced && categories.size === 1 ? [...categories][0] : 'unclassified';
        totals[category] += movement;
        details.push({ journalId, date: lines[0].date, reference: lines[0].reference ?? lines[0].source, category, amount: movement, note: !balanced ? 'Unbalanced source journal' : categories.size > 1 ? 'Mixed classifications: review source journal' : category === 'unclassified' ? 'Classification requires review' : '' });
    }
    const movement = Object.values(totals).reduce((a, b) => a + b, 0);
    return { opening, closing, movement, totals, details, reconciled: opening + movement === closing, classificationComplete: !details.some(d => d.category === 'unclassified'), cashAccounts: data.accounts.filter(a => cash.has(a.id)) };
}
export function rollforward(data: ReportSnapshot, start: string, end: string, types: string[]) { return data.accounts.filter(a => types.includes(a.type)).map(a => { const rows = data.postings.filter(p => p.accountId === a.id); const opening = rows.filter(p => p.date < start).reduce((n, p) => n + normalAmount(a, p), 0); const activity = rows.filter(p => p.date >= start && p.date <= end).reduce((n, p) => n + normalAmount(a, p), 0); return { ...a, opening, activity, closing: opening + activity }; }); }
export interface PlanMonth {
    month: string;
    receipts: number;
    payments: number;
    budgetIncome: number;
    budgetExpense: number;
}
export function forecast(opening: number, months: PlanMonth[], receiptFactor: number, paymentFactor: number) { let cash = opening; return months.map(m => { const receipts = Math.round(m.receipts * receiptFactor), payments = Math.round(m.payments * paymentFactor), start = cash; cash += receipts - payments; return { ...m, opening: start, receipts, payments, closing: cash }; }); }
export function csvCell(value: unknown) { let s = String(value ?? ''); if (typeof value !== 'number' && /^[\s]*[=+\-@\t\r]/.test(s))
    s = "'" + s; return '"' + s.replaceAll('"', '""') + '"'; }
export function csv(rows: unknown[][]) { return '\uFEFF' + rows.map(r => r.map(csvCell).join(',')).join('\r\n'); }
